import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

import { toast } from 'sonner';
import axios from 'axios';
import { Receipt, FileText, Plus, AlertTriangle, Bell, BarChart3, ScanLine, XCircle, ToggleLeft, ToggleRight, PieChart } from 'lucide-react';
import RecapContributions from './RecapContributions';

import { BACKEND_URL } from './constants';
import SouscriptionLookup from './SouscriptionLookup';
import RecuForm from './RecuForm';
import EcheancierPanel from './EcheancierPanel';
import NotificationsPanel from './NotificationsPanel';
import KPIDashboard from './KPIDashboard';
import PaymentConfigModal from './PaymentConfigModal';
import AuditVerifications from './AuditVerifications';
import RecusList from './RecusList';
import RecuResult from './RecuResult';
import { RegenererTousRecus } from './RegenererTousRecus';
import { EcartsDispositif } from './EcartsDispositif';
import { NettoyagePdf } from './NettoyagePdf';
import { getToken } from '../../services/tokenService';

export default function GenerationRecuManuel() {
  const [view, setView] = useState('list');
  const [mode, setMode] = useState('libre');
  const [selectedSub, setSelectedSub] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [recus, setRecus] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [editRecu, setEditRecu] = useState(null);
  const [echeancierRecu, setEcheancierRecu] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expandedRecuIds, setExpandedRecuIds] = useState(new Set());
  const [selectedRecus, setSelectedRecus] = useState(new Set());
  const [typeRecu, setTypeRecu] = useState('');

  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadRecus = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.append('search', search);
      if (typeRecu) params.append('type_recu', typeRecu);
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/manuels?${params}`, { headers });
      setRecus(res.data.entries || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { toast.error('Erreur chargement des Recus ESS'); }
    finally { setLoadingList(false); }
  }, [page, search, typeRecu]);

  useEffect(() => { if (view === 'list') loadRecus(); }, [view, loadRecus]);

  const handleGenerate = async (form, sub) => {
    setGenerating(true);
    setResult(null);
    try {
      let endpoint, payload;
      if (mode === 'lie' && sub) {
        endpoint = '/api/recus-ess/generer-manuel';
        payload = {
          souscription_id: sub.id,
          montant_ht: parseFloat(form.montant_ht) || null,
          montant_ttc: parseFloat(form.montant_ttc),
          taux_tva: parseFloat(form.taux_tva) || 0,
          montant_tva: parseFloat(form.montant_tva) || 0,
          date_emission: form.date_emission,
          mode_paiement: form.mode_paiement,
          modalite_paiement: form.modalite_paiement || null,
          reference_paiement: form.reference_paiement || null,
          montant_acompte: form.montant_acompte ? parseFloat(form.montant_acompte) : null,
          echeancier_details: form.echeancier_details || null,
          observations: form.observations || null,
          contributions: form.contributions || [],
        };
      } else {
        endpoint = '/api/recus-ess/generer-libre';
        payload = {
          raison_sociale: form.raison_sociale,
          siret: form.siret || '',
          adresse: form.adresse || '',
          code_postal: form.code_postal || '',
          ville: form.ville || '',
          ref_solidaire: form.ref_solidaire || '',
          numero_adherent: form.numero_adherent || '',
          objet: form.objet || 'Contribution Solidaire ESS',
          type_dispositif: form.type_dispositif || '',
          sous_type_dispositif: form.sous_type_dispositif || '',
          montant_ht: parseFloat(form.montant_ht) || null,
          montant_ttc: parseFloat(form.montant_ttc),
          taux_tva: parseFloat(form.taux_tva) || 0,
          montant_tva: parseFloat(form.montant_tva) || 0,
          date_emission: form.date_emission,
          mode_paiement: form.mode_paiement,
          modalite_paiement: form.modalite_paiement || null,
          reference_paiement: form.reference_paiement || null,
          montant_acompte: form.montant_acompte ? parseFloat(form.montant_acompte) : null,
          echeancier_details: form.echeancier_details || null,
          observations: form.observations || null,
          souscription_id: sub?.id || null,
          souscription_non_referencee: mode === 'non_ref',
          contributions: form.contributions || [],
        };
      }
      const res = await axios.post(`${BACKEND_URL}${endpoint}`, payload, { headers: { ...headers, 'Content-Type': 'application/json' } });
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur generation'); }
    finally { setGenerating(false); }
  };

  const handleAction = async (action, recu) => {
    try {
      if (action === 'view' || action === 'download' || action === 'print') {
        const res = await axios.get(`${BACKEND_URL}/api/recus-ess/${recu.id}/download`, { headers, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        if (action === 'view') { window.open(url, '_blank'); }
        else if (action === 'print') {
          const iframe = document.createElement('iframe'); iframe.style.display = 'none'; iframe.src = url;
          document.body.appendChild(iframe); iframe.onload = () => { iframe.contentWindow.print(); };
        }
        else { const a = document.createElement('a'); a.href = url; a.download = `Recu_ESS_${recu.numero_recu?.replace(/\//g, '-')}.pdf`; a.click(); window.URL.revokeObjectURL(url); }
      } else if (action === 'delete') {
        const lie = !!recu.souscription_id && !recu.is_libre && !recu.is_non_referencee;
        if (!window.confirm(`Supprimer le Recu ESS ${recu.numero_recu} ?${lie ? '\n\nIl sera aussi retiré du reçu acquitté de la souscription, qui sera régénéré.' : ''}`)) return;
        const { data } = await axios.delete(`${BACKEND_URL}/api/recus-ess/${recu.id}/supprimer`, { headers });
        toast.success(data?.message || 'Recu ESS supprime');
        loadRecus();
      } else if (action === 'ged') {
        await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/classer-ged`, {}, { headers });
        toast.success(`Recu ESS ${recu.numero_recu} classe dans la GED`);
        loadRecus();
      }
    } catch (err) { toast.error(err.response?.data?.detail || `Erreur ${action}`); }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/recus-ess/manuels/export?format=${format}`, { headers, responseType: 'blob' });
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      const mime = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a'); a.href = url; a.download = `recus_ess_manuels.${ext}`; a.click();
      toast.success(`Export ${format.toUpperCase()} telecharge`);
    } catch { toast.error('Erreur export'); }
    setExporting(false);
  };

  const toggleRecuExpand = useCallback((id) => {
    setExpandedRecuIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleRegenerate = async (recu) => {
    try {
      await axios.post(`${BACKEND_URL}/api/recus-ess/${recu.id}/regenerer`, {}, { headers });
      toast.success(`PDF regenere avec le statut actuel pour ${recu.numero_recu}`);
      loadRecus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur regeneration PDF'); }
  };

  return (
    <div className="space-y-6" data-testid="generer-recu-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm shrink-0"><Receipt className="h-8 w-8" /></div>
            <div className="min-w-0">
              <h3 className="text-2xl font-bold whitespace-nowrap" data-testid="recus-ess-title">Gestion des Reçus ESS</h3>
              <p className="text-orange-100 mt-1">Générez, modifiez, imprimez et classez vos reçus ESS</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="recus-ess-tabs">
            <Button variant={view === 'list' ? 'secondary' : 'ghost'} onClick={() => { setView('list'); setResult(null); }} className={view === 'list' ? 'bg-white text-orange-700' : 'text-white hover:bg-white/20'} data-testid="recu-view-list">
              <FileText className="h-4 w-4 mr-2" /> Mes Recus ESS
            </Button>
            <Button variant={view === 'create' ? 'secondary' : 'ghost'} onClick={() => { setView('create'); setResult(null); }} className={view === 'create' ? 'bg-white text-orange-700' : 'text-white hover:bg-white/20'} data-testid="recu-view-create">
              <Plus className="h-4 w-4 mr-2" /> Nouveau Recu ESS
            </Button>
            <Button variant={view === 'kpi' ? 'secondary' : 'ghost'} onClick={() => { setView('kpi'); setResult(null); }} className={view === 'kpi' ? 'bg-white text-orange-700' : 'text-white hover:bg-white/20'} data-testid="recu-view-kpi">
              <BarChart3 className="h-4 w-4 mr-2" /> KPI Paiements
            </Button>
            <Button variant={view === 'recap' ? 'secondary' : 'ghost'} onClick={() => { setView('recap'); setResult(null); }} className={view === 'recap' ? 'bg-white text-orange-700' : 'text-white hover:bg-white/20'} data-testid="recu-view-recap">
              <PieChart className="h-4 w-4 mr-2" /> Récap contributions
            </Button>
            <Button variant={view === 'audit' ? 'secondary' : 'ghost'} onClick={() => { setView('audit'); setResult(null); }} className={view === 'audit' ? 'bg-white text-orange-700' : 'text-white hover:bg-white/20'} data-testid="recu-view-audit">
              <ScanLine className="h-4 w-4 mr-2" /> Audit QR
            </Button>
            <Button variant="ghost" onClick={() => setShowNotifications(true)} className="text-white hover:bg-white/20" data-testid="btn-open-notifications">
              <Bell className="h-4 w-4 mr-2" /> Notifications
            </Button>
            <RegenererTousRecus onDone={() => loadRecus()} />
            <EcartsDispositif />
            <NettoyagePdf />
          </div>
        </div>
      </div>

      {view === 'kpi' && <KPIDashboard />}
      {view === 'recap' && <RecapContributions />}
      {view === 'audit' && <AuditVerifications />}

      {/* Create View */}
      {view === 'create' && !result && (
        <>
          <div className="flex items-center gap-4 p-4 bg-white rounded-xl border">
            <span className="text-sm font-medium text-gray-700">Mode :</span>
            <button onClick={() => { setMode('libre'); setSelectedSub(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'libre' ? 'bg-orange-100 text-orange-700 border border-orange-300' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="recu-mode-libre">
              {mode === 'libre' ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />} Base vierge
            </button>
            <button onClick={() => setMode('lie')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'lie' ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="recu-mode-lie">
              {mode === 'lie' ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />} Lie a une souscription
            </button>
            <button onClick={() => { setMode('non_ref'); setSelectedSub(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'non_ref' ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="recu-mode-non-ref">
              {mode === 'non_ref' ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />} Souscription non referencee
            </button>
          </div>
          {mode === 'non_ref' && (
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl flex items-start gap-3" data-testid="recu-non-ref-notice">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Souscription non referencee</p>
                <p className="text-xs text-amber-700 mt-1">Ce recu sera lie a une souscription qui ne figure pas encore dans le systeme.</p>
              </div>
            </div>
          )}
          {mode === 'lie' && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Rechercher la souscription</CardTitle></CardHeader>
              <CardContent>
                <SouscriptionLookup onSelect={sub => setSelectedSub(sub)} />
                {selectedSub && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center" data-testid="recu-selected-sub">
                    <div className="text-sm">
                      <span className="font-semibold">{selectedSub.ref_solidaire}</span>
                      <span className="text-gray-500 ml-2">{selectedSub.raison_sociale}</span>
                      <span className="text-gray-400 ml-2">SIRET: {selectedSub.siret || 'N/A'}</span>
                      <span className="text-orange-600 ml-2 font-medium">{selectedSub.montant_ttc?.toFixed(2) || '0'} EUR</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedSub(null)}><XCircle className="h-4 w-4 text-gray-400" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {(mode === 'libre' || mode === 'non_ref' || selectedSub) && (
            <Card className={`${mode === 'non_ref' ? 'border-amber-200' : 'border-orange-200'}`}>
              <CardHeader className="pb-3"><CardTitle className="text-base">{mode === 'non_ref' ? 'Recu ESS - Souscription non referencee' : 'Personnaliser le Recu ESS'}</CardTitle></CardHeader>
              <CardContent>
                <RecuForm mode={mode} selectedSub={selectedSub} onGenerate={handleGenerate} generating={generating} />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Result View */}
      {result && (
        <RecuResult
          result={result}
          onAction={handleAction}
          onNewRecu={() => { setResult(null); setView('create'); }}
          onViewAll={() => { setResult(null); setView('list'); loadRecus(); }}
        />
      )}

      {/* List View */}
      {view === 'list' && (
        <RecusList
          recus={recus} total={total} page={page} totalPages={totalPages}
          search={search} loadingList={loadingList} expandedRecuIds={expandedRecuIds}
          exporting={exporting}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          typeRecu={typeRecu} onTypeChange={(v) => { setTypeRecu(v); setPage(1); }}
          onPageChange={setPage}
          onToggleExpand={toggleRecuExpand}
          onAction={handleAction}
          onRegenerate={handleRegenerate}
          onEdit={setEditRecu}
          onEcheancier={setEcheancierRecu}
          onExport={handleExport}
          selected={selectedRecus} setSelected={setSelectedRecus} onBulkDone={loadRecus}
        />
      )}

      {editRecu && <PaymentConfigModal recu={editRecu} onClose={() => setEditRecu(null)} onRefreshList={loadRecus} />}
      {echeancierRecu && <EcheancierPanel recu={echeancierRecu} onClose={() => setEcheancierRecu(null)} onRefreshList={loadRecus} />}
      {showNotifications && <NotificationsPanel onClose={() => setShowNotifications(false)} />}
    </div>
  );
}
